// Project members (architecture sprint-12 §7.1, §9.1, A-7). The Members tab uses the oversight endpoints for
// Admins AND Supervisors (one component, one authorisation path); the Admin multi-select add and Join stay on
// /api/admin. Membership changes invalidate ['projects'], ['admin','projects'], ['me'], ['oversight'] and
// ['project-members', pid].
import { useMutation, useQuery, useQueryClient, type QueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { apiRequest, apiRequestNoContent } from '@/lib/api';
import type { AddByEmailResult, Member, MemberSuggestions, ProjectMembers, ProjectRole } from '@/types/settings';

export const projectMembersKey = (projectId: string): readonly unknown[] => ['project-members', projectId];

const oversightBase = (projectId: string): string => `/api/oversight/projects/${projectId}`;

export async function invalidateMembership(queryClient: QueryClient, projectId: string | null): Promise<void> {
  const keys: Array<readonly unknown[]> = [['projects'], ['admin', 'projects'], ['me'], ['oversight']];
  if (projectId !== null) keys.push(projectMembersKey(projectId));
  await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
}

function useInvalidateMembership(projectId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => invalidateMembership(queryClient, projectId);
}

export function useProjectMembers(projectId: string, enabled = true): UseQueryResult<ProjectMembers> {
  return useQuery({ queryKey: projectMembersKey(projectId), queryFn: () => apiRequest<ProjectMembers>(`${oversightBase(projectId)}/members`), enabled });
}

export function useMemberSuggestions(projectId: string, enabled: boolean): UseQueryResult<MemberSuggestions> {
  return useQuery({
    queryKey: [...projectMembersKey(projectId), 'suggestions'],
    queryFn: () => apiRequest<MemberSuggestions>(`${oversightBase(projectId)}/members/suggestions`),
    enabled,
  });
}

/** Admin only: the multi-select of active users (kept from sprint 11). */
export function useAddMembers(projectId: string): UseMutationResult<{ items: Member[]; added: string[]; skipped: string[] }, Error, { userIds: string[]; role: ProjectRole }> {
  const invalidate = useInvalidateMembership(projectId);
  return useMutation({
    mutationFn: (body) => apiRequest(`/api/admin/projects/${projectId}/members`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: invalidate,
  });
}

/** F1: one "Add by email" box. Errors are shown inline by the dialog (meta.silent). */
export function useAddByEmail(projectId: string): UseMutationResult<AddByEmailResult, Error, { email: string; role: ProjectRole }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiRequest<AddByEmailResult>(`${oversightBase(projectId)}/members/by-email`, { method: 'POST', body: JSON.stringify(body) }),
    meta: { silent: true },
    onSuccess: async () => {
      await invalidateMembership(queryClient, projectId);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'invites'] });
    },
  });
}

export function useChangeMemberRole(projectId: string): UseMutationResult<{ member: Member }, Error, { userId: string; role: ProjectRole }> {
  const invalidate = useInvalidateMembership(projectId);
  return useMutation({
    mutationFn: ({ userId, role }) => apiRequest(`${oversightBase(projectId)}/members/${userId}`, { method: 'PATCH', body: JSON.stringify({ role }) }),
    onSuccess: invalidate,
  });
}

export function useRemoveMember(projectId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateMembership(projectId);
  return useMutation({
    mutationFn: (userId) => apiRequestNoContent(`${oversightBase(projectId)}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useJoinProject(projectId: string): UseMutationResult<{ changed: boolean; role: ProjectRole }, Error, void> {
  const invalidate = useInvalidateMembership(projectId);
  return useMutation({
    mutationFn: () => apiRequest(`/api/admin/projects/${projectId}/join`, { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: invalidate,
  });
}

/** For the entry form's F4 recovery, where the project id is only known at submit time. */
export function useJoinAnyProject(): UseMutationResult<{ changed: boolean; role: ProjectRole }, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId) => apiRequest(`/api/admin/projects/${projectId}/join`, { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: (_data, projectId) => invalidateMembership(queryClient, projectId),
  });
}
